import { getAuthToken, getStoredProfile, storeAuthSession, clearAuthSession } from './authSession';
const BASE = import.meta.env.VITE_API_BASE_URL || '';

function getHotelId(): string {
  try {
    const profile = getStoredProfile();
    if (profile.hotel_id) return profile.hotel_id;
  } catch { }

  try {
    const token = getAuthToken();
    if (token && token.includes('.')) {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.hotel_id) return payload.hotel_id;
      }
    }
  } catch { }

  return '';
}

function authHeaders(): HeadersInit {
  const token = getAuthToken();
  const hotelId = getHotelId();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(hotelId ? { 'X-Hotel-ID': hotelId, 'X-Assujetti-ID': hotelId } : {}),
  };
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders() });
  if (res.status === 401) {
    clearAuthSession();
    window.location.href = '/login';
    throw new Error('Session expirée');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const errMsg = typeof body.error === 'string' ? body.error : (body.message || body.error?.message || `Erreur ${res.status}`);
    throw new Error(errMsg);
  }
  const json = await res.json();
  // unwrap { data: ... } envelopes
  if (json && typeof json === 'object' && 'data' in json && !Array.isArray(json)) {
    return json.data as T;
  }
  return json as T;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    clearAuthSession();
    window.location.href = '/login';
    throw new Error('Session expirée');
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const errMsg = typeof data.error === 'string' ? data.error : (data.error?.message || data.message || `Erreur ${res.status}`);
    throw new Error(errMsg);
  }
  return res.json();
}

async function downloadBlob(path: string, filename: string) {
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Téléchargement échoué');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Auth
export async function login(email: string, password: string) {
  // Try hotel entity login first, then fall back to hotel staff (hotel_users) login
  const body = JSON.stringify({ email, password });
  const headers = { 'Content-Type': 'application/json' };

  const res = await fetch(`${BASE}/api/v1/auth/hotel/login`, {
    method: 'POST', headers, body,
  });
  if (res.ok) return res.json();

  // Fallback: hotel staff login (hotel_users table)
  const userRes = await fetch(`${BASE}/api/v1/auth/hotel/user/login`, {
    method: 'POST', headers, body,
  });
  if (!userRes.ok) {
    const data = await userRes.json().catch(() => ({}));
    const errMsg = typeof data.error === 'string'
      ? data.error
      : data.error?.message || data.message || (typeof data.error?.details === 'string' ? data.error.details : '') || 'Identifiants invalides';
    throw new Error(errMsg);
  }
  return userRes.json();
}

// Hotel profile (doubles as assujetti profile for the dashboard)
export const fetchProfile = async (): Promise<any> => {
  let hid = getHotelId();
  let profileData: any = null;
  if (hid) {
    try {
      profileData = await get<any>(`/api/v1/hotels/${hid}`);
    } catch { /* fall through */ }
  }
  if (!profileData) {
    // Fallback: hotel/settings endpoint always works with hotel token
    const settings = await get<any>('/api/v1/hotel/settings');
    profileData = {
      id: settings.hotelId || settings.id || hid,
      name: settings.hotelName || settings.name,
      total_rooms: 0,
      ...settings,
    };
    if (profileData.id && !hid) {
      hid = profileData.id;
      try {
        const p = getStoredProfile();
        p.hotel_id = hid;
        if (profileData.name) p.name = profileData.name;
        storeAuthSession(getAuthToken(), p);
      } catch { }
    }
  }
  return profileData;
};

// Declarations — envelope is { declarations: [...] }
export const fetchDeclarations = async (hotelId?: string): Promise<any[]> => {
  const hid = hotelId || getHotelId();
  const query = hid ? `?hotel_id=${encodeURIComponent(hid)}` : '';
  const raw = await get<any>(`/api/v1/fpt/declarations${query}`);
  if (Array.isArray(raw)) return raw;
  return raw?.declarations || [];
};

export const validateDeclaration = (id: string) =>
  post<any>(`/api/v1/fpt/declarations/${id}/validate`, {});

export const fetchDeclarationPage = (page: number, period: string) => {
 const params = new URLSearchParams({page: String(page), page_size: '20'});
 const hotel = getHotelId(); if (hotel) params.set('hotel_id', hotel);
 if (period) params.set('period', period);
 return get<any>(`/api/v1/fpt/declarations?${params}`);
};
export const fetchDeclarationDetail = (id: string) => get<any>(`/api/v1/fpt/declarations/${encodeURIComponent(id)}`);
export async function saveDeclarationDraft(id: string, body: unknown) {
 const res = await fetch(`${BASE}/api/v1/fpt/declarations/${encodeURIComponent(id)}?workflow=draft`, {
   method: 'PUT', headers: authHeaders(), body: JSON.stringify(body),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error || 'Impossible d’enregistrer la déclaration');
 return data;
}
export const submitDeclaration = (id: string, expected_updated_at: string) =>
 post<any>(`/api/v1/fpt/declarations/${encodeURIComponent(id)}/validate?workflow=submit`, {expected_updated_at});

// Quittances — envelope is { quittances: [...] }
export const fetchQuittances = async (): Promise<any[]> => {
  try {
    const raw = await get<any>('/api/v1/fpt/quittances');
    if (Array.isArray(raw)) return raw;
    return raw?.quittances || [];
  } catch (e: any) {
    // 403 = hotel token not authorized for quittances; degrade gracefully
    if (e.message?.includes('403')) return [];
    throw e;
  }
};
export const downloadQuittance = (id: string) =>
  downloadBlob(`/api/v1/fpt/quittances/${id}/pdf`, `quittance-${id}.pdf`);

// Debit notes — envelope is { debit_notes: [...] }
export const fetchDebitNotePage = async (page=1, period=''): Promise<any> => {
 const query=new URLSearchParams({page:String(page),page_size:'20',period});
 const data=await get<any>(`/api/v1/fpt/debit-notes?${query}`);
 if (!Array.isArray(data?.debit_notes) || !Number.isInteger(data.total)) throw new Error('Réponse invalide pour les notes de débit');
 return data;
};
export const fetchDebitNotes = async (): Promise<any[]> => {
  const raw = await get<any>('/api/v1/fpt/debit-notes');
  if (Array.isArray(raw)) return raw;
  return raw?.debit_notes || [];
};
export const downloadDebitNote = (id: string) =>
  downloadBlob(`/api/v1/fpt/debit-notes/${id}/pdf`, `note-debit-${id}.pdf`);

// Payments — POST to admin endpoint (fpt/payments returns 404)
export const submitPayment = (payload: any) => post<any>('/api/v1/fpt/payments/initiate', payload);

// Establishments — the hotel profile itself represents the establishment for single-hotel users.
// For multi-establishment owners, this will return the list when the endpoint exists.
// ponytail: falls back to wrapping the hotel profile as a single-item array if the endpoint 404s.
export const fetchEstablishments = async (): Promise<any[]> => {
  try {
    return await get<any[]>('/api/v1/fpt/establishments');
  } catch {
    // Fallback: use hotel profile as single establishment
    const profile = await fetchProfile();
    return [{ id: profile.id, name: profile.name, status: profile.status, ...profile }];
  }
};

// Licenses — try hotel-scoped endpoint, fallback gracefully
export const fetchLicenses = async (estId: string): Promise<any[]> => {
  try {
    return await get<any[]>(`/api/v1/fpt/establishments/${estId}/licenses`);
  } catch {
    return [];
  }
};

export const fetchAllLicenses = async (): Promise<any[]> => {
  const ests = await fetchEstablishments();
  const all: any[] = [];
  for (const e of ests) {
    const lics = await fetchLicenses(e.id || e.establishment_id);
    all.push(...lics.map((l: any) => ({ ...l, establishment_name: e.name || e.establishment_name })));
  }
  return all;
};

// Renewals calendar
export const fetchRenewals = async (): Promise<any> => {
  try {
    const raw = await get<any>('/api/v1/fpt/renewals');
    // unwrap { data: [...] } envelope if present
    if (raw && !Array.isArray(raw) && Array.isArray(raw.data)) {
      return { items: raw.data, total: raw.total };
    }
    return raw;
  } catch {
    return { items: [], summary: {} };
  }
};

// Submit a license renewal request
export const submitRenewal = (licenseId: string, notes?: string) =>
  post<any>(`/api/v1/fpt/licenses/${licenseId}/renewals`, { notes: notes || '' });

// License rules (fee schedule)
export const fetchLicenseRules = async (): Promise<any[]> => {
  try {
    const raw = await get<any>('/api/v1/fpt/licenses');
    if (Array.isArray(raw)) return raw;
    return raw?.data || [];
  } catch {
    return [];
  }
};

// Settings — hotel profile-based
export const fetchHotelSettings = async (): Promise<any> => {
  try {
    return await get<any>('/api/v1/hotel/settings');
  } catch {
    return {};
  }
};

export const updateHotelSettings = (payload: any) =>
  post<any>('/api/v1/hotel/settings', payload);

// History — try hotel-scoped, fallback empty
export const fetchHistory = async (): Promise<any[]> => {
  try {
    return await get<any[]>('/api/v1/fpt/history');
  } catch {
    try {
      return await get<any[]>('/api/v1/admin/assujetti/me/history');
    } catch {
      return [];
    }
  }
};

// CSV export helper
export function exportCsv(rows: any[], filename: string) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => `"${r[k] ?? ''}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---- Chat / Messaging ----

export const fetchCorrespondents = async (): Promise<any[]> => {
  try {
    const raw = await get<any>('/api/v1/chat/correspondents');
    return Array.isArray(raw) ? raw : raw?.correspondents || [];
  } catch { return []; }
};

export const fetchThreads = async (status?: string): Promise<any[]> => {
  try {
    const path = status ? `/api/v1/chat/threads?status=${status}` : '/api/v1/chat/threads';
    const raw = await get<any>(path);
    if (Array.isArray(raw)) return raw;
    return raw?.threads || raw?.data || [];
  } catch { return []; }
};

export const fetchThread = async (id: string): Promise<any> =>
  get<any>(`/api/v1/chat/threads/${id}`);

export const createThread = async (recipientType?: string): Promise<any> => {
  const body: any = {};
  if (recipientType) body.recipient_type = recipientType;
  const hid = getHotelId();
  if (hid) body.hotel_id = hid;
  return post<any>('/api/v1/chat/threads', body);
};

export const fetchMessages = async (threadId: string): Promise<any[]> => {
  try {
    const raw = await get<any>(`/api/v1/chat/threads/${threadId}/messages`);
    return Array.isArray(raw) ? raw : raw?.messages || raw?.data || [];
  } catch { return []; }
};

export const sendMessage = async (threadId: string, content: string): Promise<any> =>
  post<any>(`/api/v1/chat/threads/${threadId}/messages`, { content });

export const fetchAvailability = async (): Promise<any> => {
  try { return await get<any>('/api/v1/chat/availability'); }
  catch { return { available_count: 0 }; }
};

// ---- File Attachments ----

export async function uploadAttachment(messageId: string, file: File): Promise<any> {
  const token = getAuthToken();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE}/api/v1/chat/messages/${messageId}/attachments`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: formData,
  });
  if (!res.ok) throw new Error('Upload failed');
  return res.json();
}

export async function downloadAttachment(messageId: string, attachmentId: string): Promise<void> {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/api/v1/chat/messages/${messageId}/attachments/${attachmentId}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `attachment-${attachmentId}`;
  a.click();
  URL.revokeObjectURL(url);
}

export const fetchObligations = async (page=1,period=''): Promise<any> => {
 const query=new URLSearchParams({page:String(page),period});
 const result=await get<any>(`/api/v1/fpt/obligations?${query}`);
 if(!Array.isArray(result?.obligations)||!Number.isInteger(result.total)) throw new Error('Réponse invalide pour le suivi FPT');
 return result;
};
